import React, { useState } from "react";
import states, { UnitedStatesState } from "../../data/unitedStates";
import capitalize from "../../helpers/capitalize";
import ButtonBlock from "./ButtonBlock";
import { userApi } from "../../api";
import { SubscriptionFormData } from "../../interfaces/subscriber";
import useAlert from "../../hooks/useAlert";
import useModal from "../../hooks/useModal";

const SubscribeModal = () => {
  const { setAlert } = useAlert();

  const { setShowModal } = useModal();

  const [formData, setFormData] = useState<SubscriptionFormData>({
    email: "",
    stateOfInterest: "All",
    desiredROR: "10% or more",
    desiredLoanAmount: "less than $100,000",
  });
  const [errors, setErrors] = useState({
    email: "",
    stateOfInterest: "",
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let isValid = true;
    const newErrors = { email: "", stateOfInterest: "" };

    // Email validation
    if (!formData.email) {
      newErrors.email = "Please enter your email";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // State validation
    if (!formData.stateOfInterest) {
      newErrors.stateOfInterest = "Please select a state";
      isValid = false;
    }

    setErrors(newErrors);

    if (isValid) {
      // Handle form submission logic here
      console.log(formData);
      try {
        setIsLoading(true);
        const res = await userApi.subscribeToNewsletter(formData);
        if (res.success) {
          setAlert({
            display: true,
            message: res.message,
            type: "info",
          });
        } else {
          setAlert({
            display: true,
            message: res.message,
            type: "error",
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
        setShowModal(false);
      }
    }
  };

  return (
    <div className="-mt-8">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full">
        <div className="flex flex-col gap-1 items-start w-full">
          <label htmlFor="email" className="text-main-gray font-bold">
            Email:
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            placeholder="Enter your email"
            onChange={handleChange}
            style={{ borderColor: errors.email ? "red" : "" }}
            className="input input-bordered w-full bg-white border-gray-300 focus:border-gray-500 placeholder:text-gray-400 placeholder:font-medium text-black"
            data-1p-ignore
            required
          />
          {errors.email && <p style={{ color: "red" }}>{errors.email}</p>}
        </div>
        <div className="flex flex-col gap-1 items-start">
          <label htmlFor="stateOfInterest" className="text-main-gray font-bold">
            State of Interest:
          </label>
          <select
            id="stateOfInterest"
            name="stateOfInterest"
            value={formData.stateOfInterest}
            onChange={handleChange}
            style={{ borderColor: errors.stateOfInterest ? "red" : "" }}
            required
            className="select select-bordered w-full bg-white border-gray-300 placeholder:text-gray-400 placeholder:font-medium focus:border-gray-500 text-black"
          >
            <option value="All">All States</option>
            {states.map((state: UnitedStatesState) => (
              <option key={state.value} value={state.value}>
                {capitalize(state.name)}
              </option>
            ))}
          </select>
          {errors.stateOfInterest && (
            <p style={{ color: "red" }}>{errors.stateOfInterest}</p>
          )}
        </div>
        <div className="flex flex-col gap-1 items-start">
          <label htmlFor="desiredROR" className="text-main-gray font-bold">
            Desired ROR:
          </label>
          <select
            id="desiredROR"
            name="desiredROR"
            value={formData.desiredROR}
            onChange={handleChange}
            className="select select-bordered w-full bg-white border-gray-300 placeholder:text-gray-400 placeholder:font-medium focus:border-gray-500 text-black"
          >
            <option value="10% or more">10% or more</option>
            <option value="less than 10%">less than 10%</option>
          </select>
        </div>
        <div className="flex flex-col gap-1 items-start">
          <label
            htmlFor="desiredLoanAmount"
            className="text-main-gray font-bold"
          >
            Desired Loan Amount:
          </label>
          <select
            id="desiredLoanAmount"
            name="desiredLoanAmount"
            value={formData.desiredLoanAmount}
            onChange={handleChange}
            className="select select-bordered w-full bg-white border-gray-300 placeholder:text-gray-400 placeholder:font-medium focus:border-gray-500 text-black"
          >
            <option value="less than $100,000">less than $100,000</option>
            <option value="100,000-200,000">$100,000 - $200,000</option>
            <option value="over $200,000">over $200,000</option>
          </select>
        </div>
        <div className="mt-2">
          <ButtonBlock
            handleSubmit={handleSubmit}
            submitLabel="SUBSCRIBE"
            submitting={isLoading}
            disabled={false}
            cancelLabel="CANCEL"
            onCancel={() => {}}
          />
        </div>
      </form>
    </div>
  );
};

export default SubscribeModal;
